import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useInvitationService } from "../../../hooks/useInvitationService";
import { useWorkspaceService } from "../../../hooks/useWorkspaceService";
import { useAuthService } from "../../../hooks/useAuthService";
import { activityQueryKeys } from "../constants";
import type { InviteMeta } from "../types";

/**
 * Accepts a workspace invite, refreshes the user session, and navigates
 * to the workspace. Invalidates activity queries on error to keep the
 * list fresh.
 */
export function useAcceptInviteMutation(meta: InviteMeta) {
    const invitationService = useInvitationService();
    const workspaceService = useWorkspaceService();
    const authService = useAuthService();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () =>
            invitationService.respondToInvite({
                workspaceId: meta.workspaceId,
                token: meta.token,
                action: "accept"
            }),
        onSuccess: async (response) => {
            if (response?.success) {
                await authService.refreshUserSession();
                await workspaceService.loadMyWorkspaces();
                window.location.href = meta.workspaceUrl
                    ? `/${meta.workspaceUrl}?skipSetup=true`
                    : `/workspaces/${meta.workspaceId}?skipSetup=true`;
            }
        },
        onSettled: () => queryClient.invalidateQueries({ queryKey: activityQueryKeys.all })
    });
}
